import React from 'react';
import SelectField from 'components/pure/utils/form/SelectField';
import Loading from 'components/pure/utils/Loading';
import IconButton from 'material-ui/IconButton';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import ProgressBar from 'components/pure/utils/ProgressBar';
import SkillSource from 'sources/SkillSource';
import moment from 'moment';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import round from 'utils/round';
import {Map} from 'immutable';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import './SkillContentDetail.styl';
import {getMuiV4Theme} from 'utils/css/themeUtils';

const formatWithOptionalYear = date =>
  date.year() === moment().year() ? date.format('MMMM Do') : date.format('MMMM Do YYYY');

export default class SkillContentDetail extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onSelectSkill = this.onSelectSkill.bind(this);
    this.state = {
      skillAverageBy: Map(),
      skillAverageLoadingBy: Map(),
    };
  }

  onSelectSkill(skillId) {
    const {onChange, detail, skills} = this.props;
    const selectedSkill = skills.find(skill => skill.id === skillId);
    onChange(detail.set('skillId', skillId).set('skillName', selectedSkill.skill));
    this.retrieveSkillAverages(skillId);
  }

  componentDidMount() {
    if (this.props.detail.skillId) {
      this.retrieveSkillAverages(this.props.detail.skillId);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.detail.skillId &&
      this.state.skillAverageLoadingBy.size === 0 &&
      this.state.skillAverageBy.size === 0 &&
      nextProps.recipient
    ) {
      this.retrieveSkillAverages(nextProps.detail.skillId);
    }
  }

  retrieveSkillAverages(skillId) {
    const {recipient} = this.props;
    if (!recipient || !recipient.teamId) {
      return null;
    }
    Object.entries({
      teamId: recipient.teamId,
      userId: recipient.id,
    }).forEach(([key, value]) => {
      this.setState({
        skillAverageLoadingBy: this.state.skillAverageLoadingBy.set(key, true),
      });
      SkillSource.fetchAverage(skillId, {
        [key]: value,
        from: moment().subtract(30, 'day'),
        to: moment(),
      }).then(
        average => {
          if (key === 'userId' && average) {
            this.props.onChange(this.props.detail.set('skillBaseline', average));
          }
          this.setState({
            skillAverageBy: this.state.skillAverageBy.set(key, average),
            skillAverageLoadingBy: this.state.skillAverageLoadingBy.set(key, false),
          });
        },
        () =>
          this.setState({
            skillAverageLoadingBy: this.state.skillAverageLoadingBy.set(key, false),
          })
      );
    });
  }

  render() {
    const {detail, disabled, onChange, errors, skills, recipient, coachingPlan} = this.props;
    const {skillAverageBy, skillAverageLoadingBy} = this.state;
    const {contentDetails} = coachingPlan;
    const skillOptions = skills
      .filter(
        skill =>
          !contentDetails
            .filter(otherDetail => otherDetail !== detail)
            .some(detail => detail.skillId === skill.id)
      )
      .map(skill => ({
        label: skill.skill,
        value: skill.id,
      }));
    const defaultTarget = isNil(skillAverageBy.get('userId'))
      ? 0
      : Math.round(Math.min(skillAverageBy.get('userId') + 10, 100));
    const averagesLoading = ['teamId', 'userId'].some(key => skillAverageLoadingBy.get(key));
    const selectedSkill = skills.find(skill => skill.id === detail.skillId);
    const effectiveEndDate = coachingPlan.schedule.getEffectiveEndDate();
    return (
      <div className="skill-content-detail">
        <div className="content">
          <FieldContainer labelType="div" label="Skill" error={errors ? errors.skillId : null}>
            <SelectField
              disabled={disabled}
              value={detail.skillId}
              hideResetButton={true}
              options={skillOptions}
              tether={true}
              onChange={this.onSelectSkill}
            />
          </FieldContainer>
          {averagesLoading ? (
            <Loading />
          ) : (
            detail.skillId && (
              <div>
                {!isNil(skillAverageBy.get('userId')) && selectedSkill && recipient && (
                  <p>
                    {recipient.firstName} has scored an average of{' '}
                    {round(skillAverageBy.get('userId'))}% for {selectedSkill.skill} over the past
                    30 days. What average score do you want {recipient.firstName} to achieve
                    {effectiveEndDate ? (
                      <span>
                        {' '}
                        by {formatWithOptionalYear(effectiveEndDate)}, after{' '}
                        {coachingPlan.schedule.getRepetitionCount()} meetings
                      </span>
                    ) : null}
                    ? Set up the target below:
                  </p>
                )}
                <div className="progress-bar-with-bounds">
                  <span className="bound bottom">0</span>
                  <ProgressBar
                    percentage={0}
                    color={getMuiV4Theme().palette.primary.main}
                    onSetTarget={target => onChange(detail.set('skillGoal', target))}
                    targetPercentage={isNil(detail.skillGoal) ? defaultTarget : detail.skillGoal}
                    markers={compact([
                      !isNil(skillAverageBy.get('userId')) && recipient
                        ? {
                            renderer: () => (
                              <div className="progress-label">{recipient.firstName}</div>
                            ),
                            percentage: skillAverageBy.get('userId'),
                          }
                        : null,
                      !isNil(skillAverageBy.get('teamId'))
                        ? {
                            renderer: () => <div className="progress-label">Team Avg</div>,
                            percentage: skillAverageBy.get('teamId'),
                          }
                        : null,
                    ])}
                  />
                  <span className="bound top">100</span>
                </div>
              </div>
            )
          )}
        </div>
        {this.props.showClose && (
          <IconButton onClick={this.props.onRemove}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    );
  }
}
